import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpPost } from "../../services/httpService";
import {
  getPharmacyDashboardUrl,
  getScheduleEventsByUserUrl,
  getActiveConsultationsByUserUrl,
  getPharmacyNotificationsUrl,
  markNotificationsAsReadUrl,
  markNotificationAsReadUrl,
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";

const initialState = {
  isPharmacyDashboardLoading: false,
  isPharmacyNotificationsLoading: false,
  isPharmacyScheduleLoading: false,
  isActiveConsultationsLoading: false,
  pharmacyDashboardData: null,
  userScheduleEventsData: null,
  userActiveConsultationsData: null,
  pharmacyNotificationsData: null,
};

export const getPharmacyDashboardData = createAsyncThunk(
  "pharmacyDashboard/getPharmacyDashboardData",
  async (params, thunkAPI) => {
    const { data } = params || {};
    const urlToGet = getPharmacyDashboardUrl();
    const respData = await httpPost(urlToGet, data);
    return respData;
  },
);
export const getScheduleEventsByUserData = createAsyncThunk(
  "pharmacyDashboard/getScheduleEventsByUserData",
  async (payload, thunkAPI) => {
    const { pharmacyID, userEmails } = payload || {};
    const urlToGet = getScheduleEventsByUserUrl(pharmacyID);
    const respData = await httpPost(urlToGet, userEmails);
    return respData;
  },
);
export const getActiveConsultationsByUserData = createAsyncThunk(
  "pharmacyDashboard/getActiveConsultationsByUserData",
  async (payload, thunkAPI) => {
    const { pharmacyID, userEmails } = payload || {};
    const urlToGet = getActiveConsultationsByUserUrl(pharmacyID);
    const respData = await httpPost(urlToGet, userEmails);
    return respData;
  },
);

export const getPharmacyNotificationsData = createAsyncThunk(
  "pharmacyDashboard/getPharmacyNotificationsData",
  async (pharmacyId, thunkAPI) => {
    const urlToGet = getPharmacyNotificationsUrl();
    const data = { pharmacyId: pharmacyId };
    const respData = await httpPost(urlToGet, data);
    return respData;
  },
);

export const markNotificationsAsReadData = createAsyncThunk(
  "pharmacyDashboard/markNotificationsAsReadData",
  async (pharmacyId, thunkAPI) => {
    const urlToPost = markNotificationsAsReadUrl();
    const data = { pharmacyId: pharmacyId };
    const respData = await httpPost(urlToPost, data);
    return respData;
  },
);

export const markNotificationAsReadData = createAsyncThunk(
  "pharmacyDashboard/markNotificationAsReadData",
  async (notificationId, thunkAPI) => {
    const urlToPost = markNotificationAsReadUrl();
    const data = { notificationId: notificationId };
    const respData = await httpPost(urlToPost, data);
    return respData;
  },
);

const pharmacyDashboardSlice = createSlice({
  name: "pharmacyDashboard",
  initialState,
  reducers: {
    setIsPharmacyDashboardLoading: (state, action) => {
      state.isPharmacyDashboardLoading = action?.payload || false;
    },
    setPharmacyDashboardData: (state, action) => {
      state.pharmacyDashboardData = action?.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPharmacyDashboardData.pending, (state) => {
        state.isPharmacyDashboardLoading = true;
        state.pharmacyDashboardData = null;
      })
      .addCase(getPharmacyDashboardData.fulfilled, (state, action) => {
        state.isPharmacyDashboardLoading = false;
        state.pharmacyDashboardData = action?.payload;
      })
      .addCase(getPharmacyDashboardData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPharmacyDashboardLoading = false;
        state.pharmacyDashboardData = null;
      })
      .addCase(getScheduleEventsByUserData.pending, (state) => {
        state.isPharmacyScheduleLoading = true;
        state.userScheduleEventsData = null;
      })
      .addCase(getScheduleEventsByUserData.fulfilled, (state, action) => {
        state.isPharmacyScheduleLoading = false;
        state.userScheduleEventsData = action?.payload;
      })
      .addCase(getScheduleEventsByUserData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPharmacyScheduleLoading = false;
        state.userScheduleEventsData = null;
      })
      .addCase(getActiveConsultationsByUserData.pending, (state) => {
        state.isActiveConsultationsLoading = true;
        state.userActiveConsultationsData = null;
      })
      .addCase(getActiveConsultationsByUserData.fulfilled, (state, action) => {
        state.isActiveConsultationsLoading = false;
        state.userActiveConsultationsData = action?.payload;
      })
      .addCase(getActiveConsultationsByUserData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isActiveConsultationsLoading = false;
        state.userActiveConsultationsData = null;
      })
      .addCase(getPharmacyNotificationsData.pending, (state) => {
        state.isPharmacyNotificationsLoading = true;
        state.pharmacyNotificationsData = null;
      })
      .addCase(getPharmacyNotificationsData.fulfilled, (state, action) => {
        state.isPharmacyNotificationsLoading = false;
        const notifications = action?.payload || [];
        if (Array.isArray(notifications)) {
          notifications.sort((a, b) => {
            const aCritical = a.notificationStatusType === 3 ? 0 : 1;
            const bCritical = b.notificationStatusType === 3 ? 0 : 1;
            if (aCritical !== bCritical) return aCritical - bCritical;
            return new Date(b.createdOn) - new Date(a.createdOn);
          });
        }
        state.pharmacyNotificationsData = notifications;
      })
      .addCase(getPharmacyNotificationsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isPharmacyNotificationsLoading = false;
        state.pharmacyNotificationsData = null;
      });
  },
});

export const { setIsPharmacyDashboardLoading, setPharmacyDashboardData } =
  pharmacyDashboardSlice.actions;

export default pharmacyDashboardSlice.reducer;
